import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownToLine,
  ArrowRightLeft,
  ArrowUpFromLine,
  ChevronDown,
  MessageCircle,
  Percent,
  Pencil,
  Send,
  ShoppingCart,
  Undo2,
  Wallet,
} from 'lucide-react';
import { Button, Textarea, useToast } from '@shared/ui';
import {
  formatServiceDateTime,
  type ServiceAccountingEntry,
  type ServiceAccountingOperation,
  type ServiceOpType,
} from '@entities/service-accounting';
import { formatAssetNetworkLabel } from '@shared/lib/asset-balance';
import { isNegativeDecimal, isPositiveDecimal } from '@shared/lib/format-decimal';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { useOperationComments } from '@features/service-accounting';
import { cn } from '@shared/lib/utils';

interface OperationCardProps {
  operation: ServiceAccountingOperation;
  expanded: boolean;
  onToggle: () => void;
  onRelatedOperationClick?: (operationId: string) => void;
}

const TYPE_BADGE_CLASS: Record<ServiceOpType, string> = {
  deposit: 'bg-emerald-500/15 text-emerald-700',
  withdraw: 'bg-red-500/15 text-red-700',
  exchange: 'bg-blue-500/15 text-blue-700',
  transfer: 'bg-cyan-500/15 text-cyan-700',
  expense: 'bg-orange-500/15 text-orange-700',
  refund: 'bg-violet-500/15 text-violet-700',
  adjustment: 'bg-slate-500/15 text-slate-700',
};

const TYPE_ICON = {
  deposit: ArrowDownToLine,
  withdraw: ArrowUpFromLine,
  exchange: ArrowRightLeft,
  transfer: ArrowRightLeft,
  expense: ShoppingCart,
  refund: Undo2,
  adjustment: Pencil,
} as const;

const shortInvoiceId = (invoiceId: string | null): string | null => {
  if (!invoiceId) return null;
  return invoiceId.length > 8 ? `#${invoiceId.slice(0, 8)}` : `#${invoiceId}`;
};

const LineTagBadge = ({ tag }: { tag: ServiceAccountingEntry['lineTag'] }) => {
  const { t } = useTranslation('cabinet');
  if (!tag) return null;

  if (tag === 'commission' || tag === 'service_fee') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
        <Percent className="h-2.5 w-2.5" />
        {t(`serviceAccounting.lineTags.${tag}`)}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">
      <ArrowRightLeft className="h-2.5 w-2.5" />
      {t('serviceAccounting.lineTags.transfer')}
    </span>
  );
};

const formatEntryAmount = (
  value: string | null,
  assetLabel: string | null | undefined,
  signed = false,
): string => {
  if (value == null) {
    return '—';
  }

  return formatLedgerAmount(value, { assetLabel, signed });
};

const AssetNetworkBadge = ({
  assetLabel,
  network,
}: {
  assetLabel: string | null;
  network: string | null;
}) => {
  const { t } = useTranslation('cabinet');
  if (!assetLabel && !network) {
    return null;
  }

  const label = assetLabel ?? '';
  const networkLabel = network
    ? formatAssetNetworkLabel(network, t('serviceAccounting.historicalNetwork'))
    : null;
  const text = networkLabel ? `${label} · ${networkLabel}` : label;

  return (
    <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">
      {text}
    </span>
  );
};

const CascadeLegs = ({ entries }: { entries: ServiceAccountingEntry[] }) => {
  const { t } = useTranslation('cabinet');

  return (
    <div className="flex flex-col">
      {entries.map((entry, index) => {
        const isIn = isPositiveDecimal(entry.sum);
        const DotIcon = isIn ? ArrowDownToLine : ArrowUpFromLine;
        const isFee = entry.lineTag === 'commission' || entry.lineTag === 'service_fee';
        const isTransfer = entry.lineTag === 'transfer';
        const dotFg = isFee
          ? 'text-amber-800'
          : isTransfer
            ? 'text-accent-foreground'
            : isIn
              ? 'text-emerald-700'
              : 'text-red-700';
        const dotBg = isFee
          ? 'bg-amber-500/15'
          : isTransfer
            ? 'bg-accent'
            : isIn
              ? 'bg-emerald-500/15'
              : 'bg-red-500/10';

        return (
          <div
            key={entry.id}
            className="flex gap-3"
            style={{ paddingLeft: `${index * 20}px` }}
          >
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full',
                  dotBg,
                  dotFg
                )}
              >
                <DotIcon className="h-3 w-3" />
              </div>
              {index < entries.length - 1 ? (
                <div className="my-0.5 w-0.5 min-h-5 flex-1 bg-border" />
              ) : null}
            </div>
            <div className={cn('min-w-0 flex-1', index < entries.length - 1 && 'pb-3')}>
              <div className="rounded-md border border-border bg-card px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <Wallet className="h-3 w-3" />
                    {entry.walletName}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-bold tabular-nums',
                      isPositiveDecimal(entry.sum) ? 'text-emerald-600' : 'text-red-600'
                    )}
                  >
                    {formatEntryAmount(entry.sum, entry.assetLabel, true)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>{formatServiceDateTime(entry.createdAt)}</span>
                  <span>
                    {t('serviceAccounting.table.balanceAfter')}:{' '}
                    {entry.balanceAfter == null
                      ? '—'
                      : formatEntryAmount(entry.balanceAfter, entry.assetLabel)}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  <AssetNetworkBadge assetLabel={entry.assetLabel} network={entry.network} />
                  <LineTagBadge tag={entry.lineTag} />
                </div>
                {entry.comment?.trim() ? (
                  <p className="mt-1.5 text-xs text-foreground">{entry.comment}</p>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export function OperationCard({
  operation,
  expanded,
  onToggle,
  onRelatedOperationClick,
}: OperationCardProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const {
    comments,
    commentText,
    setCommentText,
    isLoading: isCommentLoading,
    addComment,
  } = useOperationComments(operation, expanded);
  const TypeIcon = TYPE_ICON[operation.type] ?? Pencil;
  const typeBadgeClass = TYPE_BADGE_CLASS[operation.type] ?? TYPE_BADGE_CLASS.adjustment;
  const useCascade = operation.entries.length > 1;

  const timeRangeLabel = useMemo(() => {
    const from = formatServiceDateTime(operation.timeFrom, 'dd.MM, HH:mm');
    const to = formatServiceDateTime(operation.timeTo, 'dd.MM, HH:mm');
    return from === to ? from : `${from} \u2013 ${to}`;
  }, [operation.timeFrom, operation.timeTo]);

  const invoiceLabel = shortInvoiceId(operation.invoiceId);

  const handleCommentButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!expanded) onToggle();
  };

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full min-w-0 max-w-full flex-wrap items-center gap-2 px-3 py-3 text-left transition-colors hover:bg-muted/40 sm:gap-3 sm:px-4"
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            expanded && 'rotate-180'
          )}
        />

        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
            typeBadgeClass
          )}
        >
          <TypeIcon className="h-3.5 w-3.5" />
          {t(`serviceAccounting.types.${operation.type}`)}
        </span>

        <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden text-sm text-muted-foreground">
          <Wallet className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {operation.walletNames.map((name, index) => (
              <span key={`${name}-${index}`}>
                {index > 0 ? <span className="mx-1 text-muted-foreground/70">→</span> : null}
                {name}
              </span>
            ))}
          </span>
          {invoiceLabel ? (
            <span
              className="shrink-0 font-mono text-[11px] text-muted-foreground/70"
              title={operation.invoiceId ?? undefined}
            >
              · {invoiceLabel}
            </span>
          ) : null}
          <AssetNetworkBadge assetLabel={operation.assetLabel} network={operation.network} />
        </span>

        <span
          className={cn(
            'shrink-0 whitespace-nowrap text-sm font-bold tabular-nums sm:text-base',
            isPositiveDecimal(operation.displayAmount) && 'text-emerald-600',
            isNegativeDecimal(operation.displayAmount) && 'text-red-600'
          )}
        >
          {formatEntryAmount(operation.displayAmount, operation.assetLabel, true)}
        </span>

        <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground sm:text-sm">
          {timeRangeLabel}
        </span>

        <span
          role="button"
          tabIndex={0}
          onClick={handleCommentButtonClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleCommentButtonClick(event as unknown as React.MouseEvent);
            }
          }}
          className="flex shrink-0 items-center gap-1 rounded-full border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {(operation.commentsCount > 0 || comments.length > 0) && (
            <span className="inline-flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {comments.length > 0 ? comments.length : operation.commentsCount}
            </span>
          )}
        </span>
      </button>

      {expanded ? (
        <div className="border-t border-border bg-muted/30 px-3 py-3 sm:px-4 sm:py-3.5">
          <div className="space-y-4">
            {useCascade ? (
              <div className="overflow-x-auto">
                <CascadeLegs entries={operation.entries} />
              </div>
            ) : (
              <div className="-mx-1 min-w-0 px-1">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-2 py-2 font-medium">{t('serviceAccounting.table.wallet')}</th>
                      <th className="px-2 py-2 text-right font-medium">
                        {t('serviceAccounting.table.sum')}
                      </th>
                      <th className="px-2 py-2 text-right font-medium">
                        {t('serviceAccounting.table.balanceAfter')}
                      </th>
                      <th className="px-2 py-2 font-medium">{t('serviceAccounting.table.time')}</th>
                      <th className="px-2 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {operation.entries.map((entry) => (
                      <tr key={entry.id} className="border-b border-border/60 last:border-0">
                        <td className="px-2 py-2">
                          <span className="inline-flex items-center gap-1.5">
                            <Wallet className="h-3 w-3 shrink-0" />
                            <span className="truncate">{entry.walletName}</span>
                          </span>
                          {entry.comment?.trim() ? (
                            <p className="mt-1 text-xs text-foreground">{entry.comment}</p>
                          ) : null}
                        </td>
                        <td
                          className={cn(
                            'px-2 py-2 text-right font-semibold tabular-nums',
                            isPositiveDecimal(entry.sum) ? 'text-emerald-600' : 'text-red-600'
                          )}
                        >
                          {formatEntryAmount(entry.sum, entry.assetLabel, true)}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                          {entry.balanceAfter == null
                            ? '—'
                            : formatEntryAmount(entry.balanceAfter, entry.assetLabel)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">
                          {formatServiceDateTime(entry.createdAt)}
                        </td>
                        <td className="px-2 py-2">
                          <LineTagBadge tag={entry.lineTag} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {operation.relatedOperationIds.length > 0 ? (
              <div className="space-y-2 border-t border-dashed border-border pt-3">
                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('serviceAccounting.related.title')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {operation.relatedOperationIds.map((operationId) => (
                    <button
                      key={operationId}
                      type="button"
                      className="rounded-full border border-border bg-card px-2.5 py-1 font-mono text-xs text-primary hover:bg-accent"
                      onClick={() => onRelatedOperationClick?.(operationId)}
                    >
                      {operationId}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="space-y-3 border-t border-dashed border-border pt-3">
              <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <MessageCircle className="h-3 w-3" />
                {t('serviceAccounting.comments.title')}
              </h4>

              {comments.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('serviceAccounting.comments.empty')}</p>
              ) : (
                <ul className="space-y-2">
                  {comments.map((comment) => (
                    <li key={comment.id} className="flex items-start gap-2.5 text-sm">
                      {comment.authorInitials ? (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                          {comment.authorInitials}
                        </span>
                      ) : null}
                      <div className="min-w-0 flex-1 rounded-md border border-border bg-card px-2.5 py-1.5">
                        <p className="mb-0.5 text-[11px] text-muted-foreground">
                          {comment.authorName ? `${comment.authorName} · ` : ''}
                          {formatServiceDateTime(comment.createdAt)}
                        </p>
                        <p className="whitespace-pre-wrap leading-snug text-foreground">
                          {comment.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-col gap-2 sm:flex-row">
                <Textarea
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                  placeholder={t('serviceAccounting.comments.placeholder')}
                  className="min-h-9 flex-1"
                  rows={1}
                />
                <Button
                  type="button"
                  variant="default"
                  className="h-9 shrink-0 self-end rounded-md"
                  onClick={() => {
                    void addComment().then((result) => {
                      if (!result.success && result.error) {
                        toast(result.error, 'error');
                      }
                    });
                  }}
                  disabled={!commentText.trim() || isCommentLoading}
                >
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  {t('serviceAccounting.comments.add')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
